/**
 * Episode JSON schema (schema_version 1.11.0). Pure module — no DOM.
 * See docs/DATA_SCHEMA.md. Any change here requires a schema_version bump
 * and a DATA_SCHEMA.md update.
 */

import type { EncounterRecord } from "../sim/encounters";
import type {
  ControlScheme,
  AssistMode,
  MissionScore,
  Outcome,
  ResearchValidity,
  RobotProfile,
  SimEvent,
  SimSample,
  Tuning,
  Variant,
} from "../sim/types";
import type { RunReport } from "../sim/metrics";

export const SCHEMA_VERSION = "1.11.0";

export interface MissionMeta {
  /** District map identity (ADR-022). */
  map_id: string;
  /** Delivery path length (m); progress s runs 0..route_length_m. */
  route_length_m: number;
  /** Customer position (world coords). */
  destination: { x: number; z: number };
  /** Route sections by path progress. */
  sections: Array<{ section: number; name: string; piece: string; sStart: number; sEnd: number }>;
  /** Turn cues along the path (what the guidance shows). */
  turns: Array<{ s: number; kind: string; exit: number | null; label: string }>;
  /**
   * Hash of the exact map document played (fnv1a32 of its JSON), or
   * "builtin" for the compiled-in district (ADR-075). Absent before 1.9.0.
   */
  map_hash?: string;
  /** Catalogue case the run stages (e.g. "SS-PED-01"), or null (ADR-075). */
  case_id?: string | null;
}

export type PlayerType = "human" | "scripted";

export interface DeviceInfo {
  user_agent: string;
  viewport_w: number;
  viewport_h: number;
  dpr: number;
}

/** Renderer observations; never inputs to simulation state. */
export interface AppearanceRecord {
  /** Seed of visual finishes and independent pedestrian animation. Added 1.11.0. */
  variation_seed: number;
  theme_id: string;
  theme_version: string;
  config_hash: string;
  assets: "loading" | "ready" | "degraded";
  failed_assets: string[];
  fallback_policy: "same-theme-procedural";
  time_of_day: "day" | "dusk" | "night";
  weather: "clear" | "wet";
  backdrop: "procedural" | "splat";
  splat_source: string | null;
  robot_model: "bulldog" | "alaska" | "box";
  world: "suburban" | "downtown";
  quality: "full" | "lite";
  /** Full authored pack preserves local configuration edits. */
  configuration: Record<string, unknown>;
}

export interface EpisodeData {
  /** Null for headless runs; absent only in exports before 1.10.0. */
  appearance: AppearanceRecord | null;
  schema_version: string;
  app_version: string;
  /** Which camera the driver was looking through (ADR-040). */
  play_view?: "first" | "third";
  /** Base name of the video recorded alongside this run, if any (ADR-040). */
  video_base_name?: string | null;
  created_at_iso: string;
  session_id: string;
  episode_id: string;
  level_id: string;
  variant: Variant;
  mirror: 0 | 1;
  seed: number;
  attempt_number: number;
  is_first_attempt: boolean;
  player_type: PlayerType;
  scripted_player_name: string | null;
  robot_profile: RobotProfile;
  /** Selected speed profile (scales the UNVALIDATED base profile). The
   * multiplier already includes the player's tuning.speedScale. */
  speed_profile: { name: string; multiplier: number };
  /** Player tuning (Tweak panel) in force for this attempt — part of the
   * scenario identity; never pool across different tuning in analysis. */
  tuning: Tuning;
  /** Control model the joystick was read as (ADR-023): "heading" (the stick
   * points where to go) or "rate" (steering wheel + throttle). Part of the
   * condition identity — never pool across schemes. */
  control_scheme: ControlScheme;
  /** Recovery penalties accrued during the attempt. */
  penalties: { time_s: number };
  /** Research-sample validity (research level + mission; null otherwise). */
  research_validity: ResearchValidity | null;
  /** Mission metadata (mission level only; null otherwise). */
  mission: MissionMeta | null;
  /** Per-encounter telemetry segments (mission level only; null otherwise). */
  encounters: EncounterRecord[] | null;
  /** Delivery score, computed at completion (mission level only). */
  score: MissionScore | null;
  /**
   * The end-of-run report: itemised collisions and kerb climbs plus the
   * driving-quality metrics. Unlike `score`, every field here can be
   * recomputed from `samples` and `events`, which is the point of it.
   */
  report: RunReport | null;
  /** Which safety model ran: `real` never brakes before contact. */
  assist: AssistMode;
  sim: { dt_s: number; sample_rate_hz: number };
  device: DeviceInfo;
  samples: SimSample[];
  events: SimEvent[];
  outcome: Outcome;
}

export const REQUIRED_SAMPLE_FIELDS = [
  "t",
  "robot_pos",
  "robot_heading",
  "robot_speed",
  "robot_yaw_rate",
  "robot_path",
  "ped_pos",
  "ped_vel",
  "joy_dir",
  "joy_mag",
  "safety_state",
  "phase",
  "variant",
  "mirror",
  "seed",
] as const;

export const REQUIRED_TOP_LEVEL_FIELDS = [
  "schema_version",
  "app_version",
  "created_at_iso",
  "session_id",
  "episode_id",
  "level_id",
  "variant",
  "mirror",
  "seed",
  "attempt_number",
  "is_first_attempt",
  "player_type",
  "scripted_player_name",
  "robot_profile",
  "speed_profile",
  "tuning",
  "control_scheme",
  "penalties",
  "research_validity",
  "mission",
  "encounters",
  "score",
  "sim",
  "device",
  "samples",
  "events",
  "outcome",
] as const;

export const EVENT_TYPES = [
  "level_start",
  "cue_onset",
  "research_window_end",
  "first_joystick_input",
  "first_lateral_input",
  "first_commitment",
  "direction_reversal",
  "joystick_release",
  "safety_slowdown",
  "emergency_stop",
  "collision",
  "kerb_climb",
  "rough_ride",
  "fall_in",
  "adrift",
  "wrong_side",
  "passing_moment",
  "level_complete",
  "retry",
  "timeout",
  "stuck_detected",
  "recovery_started",
  "recovery_complete",
  "safe_pose_restore",
  "encounter_start",
  "encounter_end",
  "feedback",
  "abrupt_agent",
  "guidance_cue",
  "wrong_way",
  "back_on_route",
  // Living Safe Corridor (ADR-025, 1.6.0).
  "corridor_shortened",
  "pocket_created",
  "pocket_entered",
  "yield_success",
  "premature_resume",
  "amber_cleared",
  "resume_began",
  "return_right_completed",
  "lane_star",
] as const;

/** Structural validation. Returns a list of problems; empty = valid. */
export function validateEpisode(e: unknown): string[] {
  const problems: string[] = [];
  if (typeof e !== "object" || e === null) return ["episode is not an object"];
  const ep = e as Record<string, unknown>;

  for (const f of REQUIRED_TOP_LEVEL_FIELDS) {
    if (!(f in ep)) problems.push(`missing top-level field: ${f}`);
  }
  const samples = ep["samples"];
  if (!Array.isArray(samples) || samples.length === 0) {
    problems.push("samples missing or empty");
  } else {
    const first = samples[0] as Record<string, unknown>;
    for (const f of REQUIRED_SAMPLE_FIELDS) {
      if (!(f in first)) problems.push(`missing sample field: ${f}`);
    }
  }
  const events = ep["events"];
  if (!Array.isArray(events) || events.length === 0) {
    problems.push("events missing or empty");
  } else {
    for (const ev of events as Array<Record<string, unknown>>) {
      if (typeof ev["t"] !== "number") problems.push("event without t");
      if (!EVENT_TYPES.includes(ev["type"] as (typeof EVENT_TYPES)[number])) {
        problems.push(`unknown event type: ${String(ev["type"])}`);
      }
    }
    if (!(events as Array<Record<string, unknown>>).some((ev) => ev["type"] === "level_start")) {
      problems.push("missing level_start event");
    }
  }
  const outcome = ep["outcome"] as Record<string, unknown> | undefined;
  if (!outcome || typeof outcome["result"] !== "string") {
    problems.push("outcome.result missing");
  }
  // Mission blocks (1.4.0 shapes) when present.
  const mission = ep["mission"] as Record<string, unknown> | null | undefined;
  if (mission) {
    for (const f of ["map_id", "route_length_m", "destination", "sections", "turns"]) {
      if (!(f in mission)) problems.push(`missing mission field: ${f}`);
    }
    if ("destination_z" in mission) problems.push("mission.destination_z is a pre-1.4.0 field");
  }
  const encounters = ep["encounters"];
  if (Array.isArray(encounters)) {
    for (const [i, rec] of (encounters as Array<Record<string, unknown>>).entries()) {
      for (const f of ["encounter_id", "encounter_type", "section", "road_type", "encounter_valid_for_research"]) {
        if (!(f in rec)) problems.push(`encounters[${i}] missing ${f}`);
      }
    }
  }
  const score = ep["score"] as Record<string, unknown> | null | undefined;
  if (score) {
    for (const f of ["completed", "completion_time_s", "emergency_stops", "wrong_way_events", "total"]) {
      if (!(f in score)) problems.push(`missing score field: ${f}`);
    }
  }
  if (ep["control_scheme"] !== "heading" && ep["control_scheme"] !== "rate") {
    problems.push("control_scheme must be \"heading\" or \"rate\"");
  }
  const tuning = ep["tuning"] as Record<string, unknown> | undefined;
  if (!tuning || typeof tuning["sensitivity"] !== "number" || typeof tuning["speedScale"] !== "number") {
    problems.push("tuning.sensitivity/speedScale missing");
  }
  if (["1.10.0", SCHEMA_VERSION].includes(String(ep["schema_version"])) && !("appearance" in ep)) problems.push("missing top-level field: appearance");
  const appearance = ep["appearance"];
  if (appearance !== undefined && appearance !== null) {
    if (typeof appearance !== "object" || Array.isArray(appearance)) {
      problems.push("appearance must be an object or null");
    } else {
      const a = appearance as Record<string, unknown>;
      const variationSeed = a["variation_seed"];
      if (ep["schema_version"] === SCHEMA_VERSION || variationSeed !== undefined) {
        if (typeof variationSeed !== "number" || !Number.isInteger(variationSeed) || variationSeed < 0 || variationSeed > 0xffffffff) {
          problems.push("appearance.variation_seed invalid");
        }
      }
      for (const key of ["theme_id", "theme_version", "config_hash"]) {
        if (typeof a[key] !== "string" || a[key] === "") problems.push("appearance." + key + " missing");
      }
      if (!["loading", "ready", "degraded"].includes(String(a["assets"]))) problems.push("appearance.assets invalid");
      if (!Array.isArray(a["failed_assets"]) || !a["failed_assets"].every((v: unknown) => typeof v === "string")) problems.push("appearance.failed_assets invalid");
      if (a["fallback_policy"] !== "same-theme-procedural") problems.push("appearance.fallback_policy invalid");
      if (!["day", "dusk", "night"].includes(String(a["time_of_day"]))) problems.push("appearance.time_of_day invalid");
      if (!["clear", "wet"].includes(String(a["weather"]))) problems.push("appearance.weather invalid");
      if (!["procedural", "splat"].includes(String(a["backdrop"]))) problems.push("appearance.backdrop invalid");
      if (a["splat_source"] !== null && (typeof a["splat_source"] !== "string" || a["splat_source"] === "")) problems.push("appearance.splat_source invalid");
      if (a["backdrop"] === "splat" && typeof a["splat_source"] !== "string") problems.push("appearance.splat_source required for splat backdrop");
      if (!["bulldog", "alaska", "box"].includes(String(a["robot_model"]))) problems.push("appearance.robot_model invalid");
      if (!["suburban", "downtown"].includes(String(a["world"]))) problems.push("appearance.world invalid");
      if (!["full", "lite"].includes(String(a["quality"]))) problems.push("appearance.quality invalid");
      if (!a["configuration"] || typeof a["configuration"] !== "object" || Array.isArray(a["configuration"])) problems.push("appearance.configuration missing");
    }
  }
  return problems;
}
